// Headless driver transport (fn-69.2, R1) — the BFF, NOT CP-direct.
//
// The driver runs in the console (browser); the Cognito token lives in
// httpOnly BFF cookies and browser JS cannot mint CP calls. So the
// driver speaks the BFF tRPC surface (`runs.*` / `approvals.*`) — the
// SAME vanilla client the `platform-adapter` uses. (The Node e2e in
// fn-69.5 is the only CP-direct caller, with an SRP bearer.)
//
// **Why a dedicated transport and not the adapter's `startSession`:**
// `createConsolePlatformAdapter().startSession()` does `runs.start` AND
// `runs.sendMessage` in one shot — it cannot implement "create → wait
// for the fn-67 bootstrap turn to idle → THEN send the kickoff", and
// sending the kickoff while the bootstrap turn is still running 409s.
// This transport exposes a CREATE-ONLY `createSession` (no message) so
// the driver (`driver.ts`) can sequence the two calls itself.
//
// The transport is a thin, pure wrapper over a structural slice of the
// tRPC client (`HeadlessTrpcClient`), injectable for unit tests.

/** Ack kinds mirrored from the CP / BFF approvals row. */
export type AckKind =
  | 'tool_confirmation'
  | 'custom_tool_result_immediate'
  | 'custom_tool_result_deferred';

/** One `/events` envelope (preserves the `smokeball` enrichment). */
export interface HeadlessRunEvent {
  readonly id: string;
  readonly type: string;
  readonly data: Record<string, unknown>;
  readonly smokeball?: {
    readonly approval_id?: string;
    readonly decision?: 'approved' | 'rejected' | null;
    readonly decision_actor_user_id?: string | null;
  };
}

/** One pending-approval row from `approvals.list`. */
export interface HeadlessApprovalRow {
  readonly id: string;
  readonly session_id: string;
  readonly ack_kind: AckKind;
  readonly tool_kind: 'builtin' | 'mcp' | 'custom';
  readonly tool_name: string;
  readonly tool_args: Record<string, unknown>;
  readonly created_at: string;
}

export interface HeadlessCreateInput {
  readonly agentId: string;
  /** Omit for a firm-level (matter-less) session. */
  readonly matterId?: string;
  readonly metadata?: Record<string, unknown>;
  /** Account/workspace-scope mounted files (R5). */
  readonly mountedFiles?: ReadonlyArray<{ readonly fileId: string; readonly mountPath: string }>;
}

export interface HeadlessDecisionInput {
  readonly sessionId: string;
  readonly approvalId: string;
  readonly decision: 'approved' | 'rejected';
  readonly denyMessage?: string;
  readonly formData?: Record<string, unknown>;
  readonly notes?: string;
}

export interface HeadlessDecisionResult {
  readonly approval_id: string;
  readonly decision: 'approved' | 'rejected';
  readonly ack_dispatched: boolean;
  readonly ack_kind: AckKind;
  readonly redrive?: boolean;
}

/**
 * The structural slice of the BFF tRPC client the headless transport
 * consumes. The real `CreateTRPCClient<BffRouter>` is assignable to this
 * (the same way `platform-adapter`'s `InboxTrpcClient` is); tests inject
 * a fake with the same shape. NOTE `runs.start` includes `mountedFiles`
 * (the BFF accepts it — `parseStartRunInput`) which the adapter's
 * narrower slice omits.
 */
export interface HeadlessTrpcClient {
  readonly runs: {
    readonly start: {
      mutate(input: {
        agentId: string;
        matterId?: string;
        metadata?: Record<string, unknown>;
        mountedFiles?: ReadonlyArray<{ fileId: string; mountPath: string }>;
      }): Promise<{ id: string; status: string }>;
    };
    readonly byId: {
      query(input: { sessionId: string }): Promise<{ id: string; status: string }>;
    };
    readonly events: {
      query(input: {
        sessionId: string;
        afterId?: string;
        limit?: number;
        order?: 'asc' | 'desc';
      }): Promise<{ data: readonly HeadlessRunEvent[]; next_cursor?: string | null }>;
    };
    readonly sendMessage: {
      mutate(input: { sessionId: string; content: string }): Promise<unknown>;
    };
  };
  readonly approvals: {
    readonly list: {
      query(input: {
        cursor?: string;
        limit?: number;
      }): Promise<{ data: readonly HeadlessApprovalRow[]; next_cursor?: string | null }>;
    };
    readonly decide: {
      mutate(input: {
        sessionId: string;
        approvalId: string;
        decision: 'approved' | 'rejected';
        denyMessage?: string;
        formData?: Record<string, unknown>;
        notes?: string;
      }): Promise<HeadlessDecisionResult>;
    };
  };
}

export interface HeadlessTransport {
  /** CREATE-ONLY: `runs.start` with NO message (parks `pending`). */
  createSession(input: HeadlessCreateInput): Promise<{ sessionId: string; status: string }>;
  /** Current session status (used to check sendability on bootstrap timeout). */
  getSessionStatus(sessionId: string): Promise<string>;
  /** `runs.sendMessage` — the kickoff turn. */
  sendMessage(sessionId: string, content: string): Promise<void>;
  /** `runs.events` projection (preserves `smokeball` enrichment). */
  getEvents(
    sessionId: string,
    opts?: { afterId?: string; limit?: number; order?: 'asc' | 'desc' },
  ): Promise<readonly HeadlessRunEvent[]>;
  /** `approvals.list` — all caller-visible pending rows (driver filters by session). */
  listApprovals(opts?: { limit?: number }): Promise<readonly HeadlessApprovalRow[]>;
  /** `approvals.decide` — the approval/rejection ack. */
  decide(input: HeadlessDecisionInput): Promise<HeadlessDecisionResult>;
}

/** Default page size for `approvals.list` polling. */
const DEFAULT_APPROVALS_LIMIT = 200;

/**
 * Safety cap on the page walk in `listApprovals` — at 200 rows/page this
 * is 5000 visible-session approvals, far beyond any real demo firm, and
 * bounds the loop against a misbehaving/looping `next_cursor`.
 */
const MAX_APPROVAL_PAGES = 25;

/**
 * Build the headless transport over a BFF tRPC client slice. The
 * production caller passes the console's vanilla client; tests pass a
 * fake. The transport adds NO routes — it is a typed view over existing
 * `runs.*` / `approvals.*` procedures.
 */
export function createHeadlessTransport(client: HeadlessTrpcClient): HeadlessTransport {
  return {
    async createSession(input) {
      const started = await client.runs.start.mutate({
        agentId: input.agentId,
        ...(input.matterId !== undefined ? { matterId: input.matterId } : {}),
        ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
        ...(input.mountedFiles !== undefined
          ? {
              mountedFiles: input.mountedFiles.map((m) => ({
                fileId: m.fileId,
                mountPath: m.mountPath,
              })),
            }
          : {}),
      });
      return { sessionId: started.id, status: started.status };
    },

    async getSessionStatus(sessionId) {
      const row = await client.runs.byId.query({ sessionId });
      return row.status;
    },

    async sendMessage(sessionId, content) {
      await client.runs.sendMessage.mutate({ sessionId, content });
    },

    async getEvents(sessionId, opts = {}) {
      const page = await client.runs.events.query({
        sessionId,
        ...(opts.afterId !== undefined ? { afterId: opts.afterId } : {}),
        ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
        ...(opts.order !== undefined ? { order: opts.order } : {}),
      });
      return page.data;
    },

    async listApprovals(opts = {}) {
      // `GET /approvals` is paginated over the caller's VISIBLE SESSIONS,
      // so a pending row for our session can sit on a later page. Walk
      // every page (bounded) and return the flattened set; the driver
      // then filters by session_id. Without this a gate whose row lands
      // past page 1 would never resolve.
      const limit = opts.limit ?? DEFAULT_APPROVALS_LIMIT;
      const rows: HeadlessApprovalRow[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_APPROVAL_PAGES; page++) {
        const res = await client.approvals.list.query({
          limit,
          ...(cursor !== undefined ? { cursor } : {}),
        });
        rows.push(...res.data);
        const next = res.next_cursor;
        if (next === null || next === undefined || next === '') {
          break;
        }
        cursor = next;
      }
      return rows;
    },

    async decide(input) {
      return client.approvals.decide.mutate({
        sessionId: input.sessionId,
        approvalId: input.approvalId,
        decision: input.decision,
        ...(input.denyMessage !== undefined ? { denyMessage: input.denyMessage } : {}),
        ...(input.formData !== undefined ? { formData: input.formData } : {}),
        ...(input.notes !== undefined ? { notes: input.notes } : {}),
      });
    },
  };
}
